// pages/Products.js
import { useEffect, useState } from 'react';
import ProductCard from '../components/ProductCard/ProductCard';
import './Product.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [catalogUrl, setCatalogUrl] = useState(null);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // 4 columns x 5 rows = 20 products per page

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('https://infuse-backend.onrender.com/api/products');
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        setProducts(data);
        const allCategories = [...new Set(data.map(p => p.category))];
        setCategories(["All", ...allCategories]);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCatalogInfo = async () => {
      try {
        const response = await fetch('https://infuse-backend.onrender.com/api/catalog');
        if (response.ok) {
          const data = await response.json();
          setCatalogUrl(data.catalogUrl);
        }
      } catch (err) {
        console.error("Failed to fetch catalog information:", err);
      }
    };

    fetchProducts();
    fetchCatalogInfo();
}, []);

  const handleDownloadCatalog = async () => {
    try {
      setIsCatalogLoading(true);
      
      // If we already have the URL
      if (catalogUrl) {
        window.open(catalogUrl, '_blank');
        setIsCatalogLoading(false);
        return;
      }
      
      // Otherwise fetch it
      const response = await fetch('https://infuse-backend.onrender.com/api/catalog');
      if (!response.ok) throw new Error('Failed to fetch catalog');
      
      const data = await response.json();
      if (data.catalogUrl) {
        setCatalogUrl(data.catalogUrl);
        window.open(data.catalogUrl, '_blank');
      } else {
        alert('Catalog is not currently available. Please try again later.');
      }
    } catch (err) {
      console.error('Error downloading catalog:', err);
      alert('Unable to download catalog at this time. Please try again later.');
    } finally {
      setIsCatalogLoading(false);
    }
  };

  // Filter products based on selected category
  const filteredProducts = products.filter(product =>
    filter === "All" || product.category === filter
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);
  
  // Reset to first page when changing filter
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Page change handler
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    // Show max 5 pages with current page in the middle when possible
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    return (
      <div className="pagination-container">
        <button 
          className="pagination-arrow"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lt;
        </button>
        {startPage > 1 && (
          <>
            <button className="pagination-btn" onClick={() => handlePageChange(1)}>1</button>
            {startPage > 2 && <span>...</span>}
          </>
        )}
        {pages}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span>...</span>}
            <button className="pagination-btn" onClick={() => handlePageChange(totalPages)}>
              {totalPages}
            </button>
          </>
        )}
        <button 
          className="pagination-arrow"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &gt;
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading our beautiful collection...</p>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-container">
        <div className="products-header">
          <h2 className="collection-title">
            <span className="gold-underline first-text">Curated Collections</span>
          </h2>
          <p className="collection-description">
            Each piece is carefully preserved to maintain its natural beauty
          </p>

          <div className="catalog-download">
            <button 
              className="download-btn" 
              onClick={handleDownloadCatalog}
              disabled={isCatalogLoading}
            >
              {isCatalogLoading ? (
                <span>Loading...</span>
              ) : (
                <>
                  <svg className="download-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download Product Catalog
                </>
              )}
            </button>
          </div>

          <div className="category-filters">
            {categories.map(cat => (
              <button
                key={cat}
                className={`filter-btn ${filter === cat ? 'active' : ''}`}
                onClick={() => setFilter(cat)}
              >
                {cat}
                {filter === cat && <span className="active-indicator"></span>}
              </button>
            ))}
          </div>
        </div>

        {filteredProducts.length > 0 ? (
          <>
            <div className="products-grid">
              {currentProducts.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
            {renderPagination()}
          </>
        ) : (
          <div className="empty-state">
            <img src="/images/empty-flower.png" alt="No products" className="empty-icon" />
            <h3>No products found in this category</h3>
            <button className="reset-filter" onClick={() => setFilter("All")}>
              View All Collections
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;